(() => {
  const layouts = document.querySelectorAll('[data-product-description-layout]');
  if (!layouts.length) return;

  const emojiPattern = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\uFE0E\uFE0F]/gu;
  const leadingEmojiPattern = /^[\s\u200B-\u200D\uFEFF]*[\p{Extended_Pictographic}]/u;
  const invisiblePattern = /[\u200B-\u200D\uFEFF]/g;

  const cleanText = value => String(value || '')
    .replace(invisiblePattern, '')
    .replace(emojiPattern, '')
    .replace(/\s+/g, ' ')
    .trim();

  const comparableText = value => cleanText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .replace(/^(le|la|les|un|une)/, '');

  const cleanContent = container => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(node => {
      node.nodeValue = node.nodeValue
        .replace(invisiblePattern, '')
        .replace(emojiPattern, '')
        .replace(/\s+([:;!?])/g, '$1');
    });
  };

  const hasLeadingEmoji = element => leadingEmojiPattern.test(element.textContent || '');

  const isHeadingElement = element => /^H[1-6]$/.test(element?.tagName || '');

  const createEditorialHeading = element => {
    const heading = document.createElement('h3');
    heading.className = 'product-editorial-heading';
    heading.innerHTML = element.innerHTML;
    element.replaceWith(heading);
    return heading;
  };

  const normalizeSourceBlocks = source => {
    Array.from(source.childNodes).forEach(node => {
      if (node.nodeType !== Node.TEXT_NODE || !cleanText(node.nodeValue)) return;
      const paragraph = document.createElement('p');
      paragraph.textContent = node.nodeValue;
      node.replaceWith(paragraph);
    });

    Array.from(source.children).forEach(element => {
      if (!['P', 'DIV'].includes(element.tagName)) return;
      const breakCount = element.querySelectorAll('br').length;
      const shouldSplit = breakCount > 1
        || (breakCount === 1 && /composition\s*:/i.test(element.textContent || ''));
      if (!shouldSplit) return;

      const parts = element.innerHTML
        .split(/<br\s*\/?\s*>/i)
        .map(part => part.trim())
        .filter(part => cleanText(part.replace(/<[^>]+>/g, '')));
      if (parts.length < 2) return;

      const fragment = document.createDocumentFragment();
      parts.forEach(part => {
        const paragraph = document.createElement('p');
        paragraph.innerHTML = part;
        fragment.appendChild(paragraph);
      });
      element.replaceWith(fragment);
    });
  };

  const extractComposition = (source, target, item) => {
    const children = Array.from(source.children);
    let marker = null;
    let inlineComposition = '';

    for (const child of children) {
      const text = cleanText(child.textContent);
      const exactMatch = text.match(/^composition(?:\s+inci)?\s*:?\s*$/i);
      const inlineMatch = text.match(/^composition(?:\s+inci)?\s*:\s*(.+)$/i);
      if (exactMatch || inlineMatch) {
        marker = child;
        inlineComposition = inlineMatch?.[1] || '';
        break;
      }
    }

    if (!marker) return false;

    if (inlineComposition) {
      const paragraph = document.createElement('p');
      paragraph.textContent = inlineComposition;
      target.appendChild(paragraph);
    }

    let sibling = marker.nextSibling;
    while (sibling) {
      const nextSibling = sibling.nextSibling;
      target.appendChild(sibling);
      sibling = nextSibling;
    }
    marker.remove();

    cleanContent(target);
    if (cleanText(target.textContent)) {
      item.hidden = false;
      target.classList.add('product-composition-panel');
      return true;
    }
    return false;
  };

  const removeRepeatedTitle = source => {
    const firstElement = Array.from(source.children).find(element => cleanText(element.textContent));
    if (!firstElement) return;

    const productTitle = comparableText(source.dataset.productTitle);
    const firstText = comparableText(firstElement.textContent);
    if (!productTitle || !firstText) return;

    const sameTitle = productTitle === firstText;
    const nearlySameTitle = firstText.length > 18
      && (productTitle.startsWith(firstText) || firstText.startsWith(productTitle))
      && Math.abs(productTitle.length - firstText.length) < 24;

    if (sameTitle || nearlySameTitle) firstElement.remove();
  };

  const buildIngredientTable = source => {
    const children = Array.from(source.children);
    for (let index = 0; index < children.length - 3; index += 1) {
      const firstHeader = children[index];
      const secondHeader = children[index + 1];
      const firstHeaderText = cleanText(firstHeader.textContent);
      const secondHeaderText = cleanText(secondHeader.textContent);

      if (!/^(ingr[eé]dient|actif)/i.test(firstHeaderText)) continue;
      if (!/^(action|bienfait|b[eé]n[eé]fice|r[oô]le)/i.test(secondHeaderText)) continue;

      const rows = [];
      let cursorIndex = index + 2;
      while (cursorIndex + 1 < children.length) {
        const labelElement = children[cursorIndex];
        const bodyElement = children[cursorIndex + 1];
        const label = cleanText(labelElement.textContent);
        const body = cleanText(bodyElement.textContent);

        if (!label || !body) break;
        if (isHeadingElement(labelElement) || isHeadingElement(bodyElement)) break;
        if (hasLeadingEmoji(labelElement) || hasLeadingEmoji(bodyElement)) break;
        if (!['P', 'DIV'].includes(labelElement.tagName) || !['P', 'DIV'].includes(bodyElement.tagName)) break;
        if (/^(composition|en r[eé]sum[eé]|conseil|innovation)/i.test(label)) break;

        rows.push({ label, body, labelElement, bodyElement });
        cursorIndex += 2;
      }

      if (!rows.length) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'product-editorial-table-wrap';
      const table = document.createElement('table');
      table.className = 'product-editorial-table';
      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      [firstHeaderText, secondHeaderText].forEach(text => {
        const th = document.createElement('th');
        th.textContent = text;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      rows.forEach(row => {
        const tr = document.createElement('tr');
        const titleCell = document.createElement('td');
        const bodyCell = document.createElement('td');
        titleCell.textContent = row.label;
        bodyCell.textContent = row.body;
        tr.append(titleCell, bodyCell);
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      wrapper.appendChild(table);
      firstHeader.before(wrapper);

      firstHeader.remove();
      secondHeader.remove();
      rows.forEach(row => {
        row.labelElement.remove();
        row.bodyElement.remove();
      });
      return true;
    }
    return false;
  };

  const enhanceHeadings = source => {
    Array.from(source.children).forEach(element => {
      if (isHeadingElement(element)) {
        element.classList.add('product-editorial-heading');
        return;
      }
      if (element.tagName !== 'P') return;

      const text = cleanText(element.textContent);
      const isStrongOnly = element.children.length === 1 && ['STRONG', 'B'].includes(element.firstElementChild?.tagName);
      const isShort = text.length > 3 && text.length < 140;
      const isSummary = /^en r[eé]sum[eé]\s*:/i.test(text);
      const isRecommendation = /(se combine|peut se combiner|association recommand[eé]e|conseil professionnel)/i.test(text);

      if (isSummary) {
        element.classList.add('product-summary-callout');
      } else if (isRecommendation) {
        element.classList.add('product-professional-note');
      } else if (isShort && (hasLeadingEmoji(element) || isStrongOnly)) {
        createEditorialHeading(element);
      }
    });
  };

  const enhanceLists = source => {
    Array.from(source.children).forEach(element => {
      if (!['UL', 'OL'].includes(element.tagName)) return;
      const previousText = cleanText(element.previousElementSibling?.textContent);
      const isBenefitList = element.tagName === 'UL'
        && (/pourquoi|b[eé]n[eé]fice|point fort|indispensable/i.test(previousText)
          || Array.from(element.children).filter(item => cleanText(item.textContent).includes(':')).length >= 2);

      element.classList.add(isBenefitList ? 'product-benefit-grid' : 'product-editorial-list');

      if (!isBenefitList) return;
      Array.from(element.children).forEach(item => {
        if (item.querySelector('strong, b')) return;
        const text = cleanText(item.textContent);
        const separatorIndex = text.indexOf(':');
        if (separatorIndex < 1 || separatorIndex > 90) return;

        const title = text.slice(0, separatorIndex).trim();
        const body = text.slice(separatorIndex + 1).trim();
        const strong = document.createElement('strong');
        const span = document.createElement('span');
        strong.textContent = title;
        span.textContent = body;
        item.replaceChildren(strong, span);
      });
    });
  };

  const enhanceTables = source => {
    source.querySelectorAll('table').forEach(table => {
      table.classList.add('product-editorial-table');
      if (table.parentElement?.classList.contains('product-editorial-table-wrap')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'product-editorial-table-wrap';
      table.before(wrapper);
      wrapper.appendChild(table);
    });
  };

  const enhanceIntro = source => {
    let leadCount = 0;
    for (const element of source.children) {
      if (isHeadingElement(element) || ['UL', 'OL', 'TABLE'].includes(element.tagName) || element.classList.contains('product-editorial-table-wrap')) break;
      if (element.tagName === 'P' && !element.classList.contains('product-summary-callout')) {
        element.classList.add('product-editorial-lead');
        leadCount += 1;
        if (leadCount === 2) break;
      }
    }
  };

  layouts.forEach(layout => {
    const source = layout.querySelector('[data-product-description-source]');
    if (!source) return;

    normalizeSourceBlocks(source);

    const compositionItem = layout.querySelector('[data-product-composition-item]');
    const compositionTarget = layout.querySelector('[data-product-composition-target]');
    if (compositionItem && compositionTarget) {
      extractComposition(source, compositionTarget, compositionItem);
    }

    removeRepeatedTitle(source);
    buildIngredientTable(source);
    enhanceHeadings(source);
    cleanContent(source);
    enhanceLists(source);
    enhanceTables(source);
    enhanceIntro(source);

    source.querySelectorAll('p').forEach(paragraph => {
      if (!cleanText(paragraph.textContent) && !paragraph.querySelector('img, video, iframe')) paragraph.remove();
    });
    source.classList.add('is-enhanced');
  });
})();
